import { FC } from 'react';
import dayjs from 'dayjs';
import { IActivity as Activity } from '@/utils/activity-calendar/interfaces/IActivity';
import {
  DAYS_PER_WEEK,
  FIRST_DAY_OF_MONTH,
  MONDAY_FIRST_WEEK_OFFSET,
  MONTHS_FULL,
  MONTHS_PER_YEAR,
  TYPE_META,
} from '@/utils/activity-calendar/constants';

interface IProps {
  idx: Record<string, Activity[]>;
  year: number;
  today: dayjs.Dayjs;
  minMonthKey: number;
  maxMonthKey: number;
  onOpenMonth: (year: number, monthIndex: number) => void;
}

const MiniMonth: FC<{
  year: number;
  monthIndex: number;
  idx: IProps['idx'];
  today: dayjs.Dayjs;
  present: boolean;
  onOpenMonth: IProps['onOpenMonth'];
}> = ({ year, monthIndex, idx, today, present, onOpenMonth }) => {
  const first = dayjs(new Date(year, monthIndex, FIRST_DAY_OF_MONTH));
  const startOff = (first.day() + MONDAY_FIRST_WEEK_OFFSET) % DAYS_PER_WEEK;
  const dim = first.daysInMonth();

  const cells: JSX.Element[] = [];
  for (let dayOffset = 0; dayOffset < startOff; dayOffset++) {
    cells.push(
      <div
        key={`blank-${year}-${monthIndex}-${dayOffset}`}
        className="h-[15px] rounded-[3px]"
        style={{ background: '#EFEFEF' }}
      />,
    );
  }
  let total = 0;
  for (let dayOfMonth = FIRST_DAY_OF_MONTH; dayOfMonth <= dim; dayOfMonth++) {
    const acts = idx[`${year}-${monthIndex}-${dayOfMonth}`] || [];
    total += acts.length;
    const isToday = first.date(dayOfMonth).isSame(today, 'day');
    cells.push(
      <div
        key={dayOfMonth}
        className="flex h-[15px] items-center justify-center rounded-[3px]"
        style={{ background: isToday ? '#e3f1ea' : '#F7F8F5' }}
      >
        {acts.length > 0 && (
          <div className="flex items-center gap-[2px]">
            {TYPE_META.filter((t) => acts.some((a) => a.type === t.key)).map((t) => (
              <span key={t.key} className="block h-[5px] w-[5px] rounded-full" style={{ background: t.color }} />
            ))}
          </div>
        )}
      </div>,
    );
  }

  return (
    <button
      type="button"
      onClick={present ? () => onOpenMonth(year, monthIndex) : undefined}
      className="flex flex-col gap-[9px] rounded-[10px] border bg-white px-[12px] pb-[11px] pt-[12px] text-left font-sans"
      style={{ borderColor: '#E5E7EB', opacity: present ? 1 : 0.4, cursor: present ? 'pointer' : 'default' }}
    >
      <div className="flex items-center justify-between">
        <span className="font-display text-[14px] font-bold text-[#1F2937]">{MONTHS_FULL[monthIndex]}</span>
        {total > 0 && (
          <span className="rounded-full bg-secondary px-[8px] py-[2px] text-[11px] font-bold text-primary">
            {total}
          </span>
        )}
      </div>
      <div className="grid grid-cols-7 gap-[2px]">{cells}</div>
    </button>
  );
};

export const YearView: FC<IProps> = ({ idx, year, today, minMonthKey, maxMonthKey, onOpenMonth }) => (
  <div className="grid grid-cols-2 gap-[14px] min-[700px]:grid-cols-3 min-[1000px]:grid-cols-4">
    {Array.from({ length: MONTHS_PER_YEAR }, (_, monthIndex) => {
      const monthKey = year * MONTHS_PER_YEAR + monthIndex;
      const present = monthKey >= minMonthKey && monthKey <= maxMonthKey;
      return (
        <MiniMonth
          key={monthIndex}
          year={year}
          monthIndex={monthIndex}
          idx={idx}
          today={today}
          present={present}
          onOpenMonth={onOpenMonth}
        />
      );
    })}
  </div>
);
